import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EmployeeCard } from '../EmployeeCard/EmployeeCard';
import { CallMethodModal } from '../CallMethodModal/CallMethodModal';
import { Building2 } from 'lucide-react';
import { TenureChip } from '../TenureBadge/TenureBadge';
import { getProfile } from '../../services/profileService';
import { getNameSortKey } from '../../utils/nameUtils';
import { abbreviateTitle, isNameSort } from '../../utils/employeeSort';
import { DirectoryColumnBand } from './DirectoryTableHeader';
import { DEFAULT_DENSITY, getDensity, loadDensity } from './rowLayout';
import { teamsChatUrl, teamsCallUrl, telUrl } from '../../utils/deepLinks';
import { AlphabetNav } from '../AlphabetNav/AlphabetNav';

// The list container. Now a token class rather than two hand-written rgba pairs
// behind a useIsDark() branch — see the token block in src/index.css.
const LIST_SURFACE = 'bg-surface border border-line shadow-e1';

function getAvailabilityColor(availability) {
  if (!availability) return 'bg-slate-300';
  const s = availability.toLowerCase();
  if (s === 'available' || s === 'availableidle') return 'bg-emerald-500';
  if (s === 'busy' || s === 'busyidle') return 'bg-red-500';
  if (s === 'away') return 'bg-yellow-500';
  if (s === 'donotdisturb' || s === 'dnd') return 'bg-red-600';
  if (s === 'berightback' || s === 'brb') return 'bg-yellow-300';
  if (s === 'offline') return 'bg-slate-400';
  return 'bg-slate-300';
}

function getAvailabilityTooltip(availability) {
  if (availability === 'Available' || availability === 'available') return 'Available - Ready to chat';
  if (availability === 'Busy' || availability === 'busy') return 'Busy - In a meeting or focused';
  if (availability === 'Away' || availability === 'away') return 'Away - Stepped away';
  if (availability === 'DoNotDisturb' || availability === 'dnd') return "Do Not Disturb - Please don't interrupt";
  if (availability === 'Offline' || availability === 'offline') return 'Offline - Not available';
  return 'Status unknown';
}

// Defined at module scope so its identity is stable across EmployeeGrid renders.
// Previously defined inside EmployeeGrid, which caused React to see a new component
// type on every render and unmount/remount all rows (the flicker).
function EmployeeListRow({
  employee,
  index,
  photo,
  isSelected,
  isFavorite,
  hasAnySelected,
  onToggleSelection,
  onToggleFavorite,
  onEmployeeClick,
  onBadgesClick,
  density = DEFAULT_DENSITY,
}) {
  const [showCallModal, setShowCallModal] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const d = getDensity(density);
  const fullTitle = employee.jobTitle || 'Employee';
  const shortTitle = abbreviateTitle(fullTitle);

  const initials = employee.displayName
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const hasCallOptions = employee.mobilePhone || employee.businessPhones?.[0] || employee.mail;
  const showCheckbox = isHovered || hasAnySelected;

  const handleCallClick = (e) => {
    e.stopPropagation();
    const hasMobile = !!employee.mobilePhone;
    const hasBusiness = !!(employee.businessPhones?.[0]);
    const hasTeams = !!employee.mail;
    const methodCount = [hasMobile, hasBusiness, hasTeams].filter(Boolean).length;
    if (methodCount > 1) {
      setShowCallModal(true);
    } else if (hasMobile) {
      window.location.href = telUrl(employee.mobilePhone);
    } else if (hasBusiness) {
      window.location.href = telUrl(employee.businessPhones[0]);
    } else if (hasTeams) {
      {
        const url = teamsCallUrl(employee.mail);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const accentColor = getAvailabilityColor(employee.availability);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.22, delay: index * 0.02 }}
        onClick={() => onEmployeeClick?.(employee)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`group relative grid items-center gap-4 px-3.5 cursor-pointer transition-colors duration-150
          ${d.gridClass} ${d.rowHeightClass}
          border-b border-line last:border-b-0
          before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[2px] before:bg-accent
          before:opacity-0 hover:before:opacity-100
          ${isSelected ? 'bg-accent-soft before:opacity-100' : 'bg-transparent hover:bg-accent-soft'}`}
      >
        {/* Avatar with overlaid checkbox + presence dot */}
        <div className="flex-shrink-0 relative">
          <motion.button
            onClick={(e) => { e.stopPropagation(); onToggleSelection?.(employee); }}
            initial={false}
            animate={{ opacity: showCheckbox ? 1 : 0, scale: showCheckbox ? 1 : 0.75 }}
            transition={{ duration: 0.13 }}
            className={`absolute -top-1 -left-1 z-10 w-5 h-5 rounded-full border-2 flex items-center justify-center
              transition-colors duration-150 shadow-sm
              ${isSelected ? 'bg-primary-500 border-primary-500 text-white' : 'bg-white dark:bg-zinc-800 border-slate-300 dark:border-zinc-500 hover:border-primary-400'}
              ${!showCheckbox ? 'pointer-events-none' : ''}`}
          >
            {isSelected && (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </motion.button>

          {photo ? (
            <img
              src={photo}
              alt={employee.displayName}
              className={`${d.avatarClass} rounded-full object-cover object-top ring-1 ring-line-strong`}
            />
          ) : (
            <div className={`${d.avatarClass} rounded-full bg-surface-2 flex items-center justify-center ring-1 ring-line-strong`}>
              <span className="text-[11px] font-semibold text-ink-3 select-none">{initials}</span>
            </div>
          )}

          {/* Presence dot — avatar corner only */}
          <div
            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-surface ${accentColor}`}
            title={getAvailabilityTooltip(employee.availability)}
          />
        </div>

        {/* Name + title — the only column allowed to absorb slack */}
        <div className="min-w-0">
          {d.stacked ? (
            <>
              <p className="text-[13.5px] font-semibold text-ink truncate leading-tight tracking-[-0.01em]">
                {employee.displayName}
              </p>
              {/* Abbreviated for room; the untruncated title stays in the tooltip */}
              <p
                className="text-[11.5px] text-ink-3 truncate mt-[1px] leading-snug"
                title={fullTitle}
              >
                {shortTitle}
              </p>
            </>
          ) : (
            <p className="flex items-baseline gap-2 min-w-0 leading-none" title={fullTitle}>
              <span className="text-[13.5px] font-semibold text-ink truncate flex-none max-w-[55%] tracking-[-0.01em]">
                {employee.displayName}
              </span>
              <span className="text-[11.5px] text-ink-3 truncate">
                {shortTitle}
              </span>
            </p>
          )}
        </div>

        {/* Tenure — a numeral in a fixed column, not a stack of emoji */}
        <div className="hidden md:block">
          <TenureChip
            hireDate={employee.hireDate}
            onClick={onBadgesClick ? () => onBadgesClick(employee) : undefined}
          />
        </div>

        {/* Department — AA contrast; de-emphasised by size and position, not by fading out */}
        <div className="hidden md:block min-w-0">
          <span className="block text-[12.5px] text-ink-2 truncate" title={employee.department || ''}>
            {employee.department || '—'}
          </span>
        </div>

        {/* Office */}
        <div className="hidden lg:block min-w-0">
          <span className="block text-[12px] text-ink-3 truncate" title={employee.officeLocation || ''}>
            {employee.officeLocation || '—'}
          </span>
        </div>

        {/* Spacer — absorbs all leftover width so the data columns stay
            clustered left instead of drifting apart on a wide window */}
        <div className="hidden md:block" aria-hidden="true" />

        {/* Actions — Call, Message and Favorite as one always-visible labeled
            group. These used to be icon-only and hover-revealed, which hid
            them from anyone who hadn't discovered the hover affordance. */}
        <div
          className="flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {hasCallOptions && (
            <button
              onClick={handleCallClick}
              title={`Call ${employee.displayName}`}
              aria-label={`Call ${employee.displayName}`}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium
                         text-accent hover:bg-accent-soft transition-colors"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="hidden lg:inline">Call</span>
            </button>
          )}
          {employee.mail && (
            <a
              href={teamsChatUrl(employee.mail)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={`Message ${employee.displayName} in Teams`}
              aria-label={`Message ${employee.displayName} in Teams`}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium
                         text-[#6264A7] dark:text-[#8B8CC7] hover:bg-[#6264A7]/10 dark:hover:bg-[#8B8CC7]/15 transition-colors"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="hidden lg:inline">Message</span>
            </a>
          )}
          {!isSelected && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(employee.id); }}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium transition-colors
                ${isFavorite ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20' : 'text-slate-500 dark:text-zinc-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'}`}
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={isFavorite ? 0 : 2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              <span className="hidden lg:inline">Favorite</span>
            </button>
          )}
        </div>
      </motion.div>

      {showCallModal && (
        <CallMethodModal employee={employee} onClose={() => setShowCallModal(false)} />
      )}
    </>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= currentPage - 2 && i <= currentPage + 2)) {
      pages.push(i);
    }
  }

  const items = [];
  for (let i = 0; i < pages.length; i++) {
    if (i > 0 && pages[i] - pages[i - 1] > 1) items.push('…');
    items.push(pages[i]);
  }

  return (
    <div className="flex items-center justify-center gap-1 pt-8 pb-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Prev
      </button>

      {items.map((item, i) =>
        item === '…' ? (
          <span key={`e${i}`} className="w-9 text-center text-slate-400 dark:text-zinc-500 text-sm select-none">…</span>
        ) : (
          <button
            key={item}
            onClick={() => onPageChange(item)}
            className={`w-9 h-9 text-sm rounded-lg border transition-colors ${
              item === currentPage
                ? 'bg-primary-500 border-primary-500 text-white font-semibold'
                : 'border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800'
            }`}
          >
            {item}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors"
      >
        Next
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
      </button>
    </div>
  );
}

/**
 * Grid/List layout for employee cards with pagination
 */
export function EmployeeGrid({
  employees,
  photos,
  onEmployeeClick,
  groupByDepartment = false,
  groupByOffice = false,
  sortBy = 'lastName',
  selectedEmployees = [],
  onToggleSelection,
  favorites = [],
  onToggleFavorite,
  displayMode = 'card',
  currentUserId = null,
  onBadgesClick,
  onSortChange,
  availableLetters = [],
  selectedLetter = null,
  onLetterClick,
}) {
  const getLetterKey = (displayName) =>
    (getNameSortKey(displayName, sortBy).charAt(0) || '#').toUpperCase();
  const [currentPage, setCurrentPage] = useState(1);
  const [density] = useState(loadDensity);
  const containerRef = useRef(null);
  const letterRefs = useRef({});

  const PAGE_SIZE = displayMode === 'list' ? 30 : 24;

  // Reset to page 1 when the employee list changes (filter / sort applied)
  useEffect(() => {
    setCurrentPage(1);
  }, [employees]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const isSelected = (employeeId) => selectedEmployees.some((e) => e.id === employeeId);
  const isFav = (employeeId) => favorites.includes(employeeId);

  if (!groupByDepartment && !groupByOffice) {
    const totalPages = Math.ceil(employees.length / PAGE_SIZE);
    const pageEmployees = employees.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    if (displayMode === 'list') {
      const columnBand = (
        <DirectoryColumnBand density={density} sortBy={sortBy} onSortChange={onSortChange} />
      );

      // A first-letter index only means something when the list is sorted by
      // name. Under a department, office or tenure sort the letter headers would
      // be in arbitrary order, so the rows run as one continuous table instead.
      if (!isNameSort(sortBy)) {
        return (
          <div ref={containerRef} className="w-full scroll-mt-28">
            <div className={`rounded-card overflow-hidden ${LIST_SURFACE}`}>
              {columnBand}
              {pageEmployees.map((employee, index) => {
                const customProfile = getProfile(employee.id);
                return (
                  <EmployeeListRow
                    key={employee.id}
                    employee={employee}
                    index={index}
                    density={density}
                    photo={customProfile?.photo || employee.openAssetPhoto || photos[employee.id]}
                    isSelected={isSelected(employee.id)}
                    isFavorite={isFav(employee.id)}
                    hasAnySelected={selectedEmployees.length > 0}
                    onBadgesClick={onBadgesClick}
                    onToggleSelection={onToggleSelection}
                    onToggleFavorite={onToggleFavorite}
                    onEmployeeClick={onEmployeeClick}
                  />
                );
              })}
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        );
      }

      const groupedByLetter = pageEmployees.reduce((acc, employee) => {
        const firstLetter = getLetterKey(employee.displayName);
        if (!acc[firstLetter]) acc[firstLetter] = [];
        acc[firstLetter].push(employee);
        return acc;
      }, {});
      const sortedLetters = Object.keys(groupedByLetter).sort();

      // One card for the whole page, with sticky letter bands inside it. Each
      // letter used to get its own card and a loose grey letter floating above
      // it, which broke the list into a dozen disconnected slabs.
      return (
        <div ref={containerRef} className="w-full scroll-mt-28">
          <div className="relative lg:pl-10">
            {availableLetters.length > 0 && (
              <AlphabetNav
                availableLetters={availableLetters}
                selectedLetter={selectedLetter}
                onLetterClick={onLetterClick}
              />
            )}
            <div className={`rounded-card overflow-hidden ${LIST_SURFACE}`}>
              {columnBand}
              {sortedLetters.map((letter) => (
                <div key={letter} ref={(el) => (letterRefs.current[letter] = el)}>
                  <div className="sticky top-0 z-[5] flex items-center gap-2.5 px-3.5 py-2 bg-surface-2 border-b border-line">
                    <span className="text-[12px] font-extrabold tracking-[0.04em] text-accent-ink">{letter}</span>
                    <span className="font-mono text-[10.5px] text-ink-4">
                      {groupedByLetter[letter].length}
                    </span>
                  </div>
                  <div>
                    {groupedByLetter[letter].map((employee, index) => {
                      const customProfile = getProfile(employee.id);
                      return (
                        <EmployeeListRow
                          key={employee.id}
                          employee={employee}
                          index={index}
                          density={density}
                          photo={customProfile?.photo || employee.openAssetPhoto || photos[employee.id]}
                          isSelected={isSelected(employee.id)}
                          isFavorite={isFav(employee.id)}
                          hasAnySelected={selectedEmployees.length > 0}
                          onBadgesClick={onBadgesClick}
                          onToggleSelection={onToggleSelection}
                          onToggleFavorite={onToggleFavorite}
                          onEmployeeClick={onEmployeeClick}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      );
    }

    const letterGroups = pageEmployees.reduce((acc, employee) => {
      const firstLetter = getLetterKey(employee.displayName);
      if (!acc[firstLetter]) acc[firstLetter] = [];
      acc[firstLetter].push(employee);
      return acc;
    }, {});

    return (
      <div ref={containerRef} className="w-full scroll-mt-28">
        <div className="relative lg:pl-10">
          {isNameSort(sortBy) && availableLetters.length > 0 && (
            <AlphabetNav
              availableLetters={availableLetters}
              selectedLetter={selectedLetter}
              onLetterClick={onLetterClick}
            />
          )}
          <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(212px,1fr))]">
            <AnimatePresence mode="popLayout">
              {pageEmployees.map((employee, index) => {
                const firstLetter = getLetterKey(employee.displayName);
                const isFirstOfLetter = letterGroups[firstLetter]?.[0]?.id === employee.id;
                return (
                  <div
                    key={employee.id}
                    ref={isFirstOfLetter ? (el) => (letterRefs.current[firstLetter] = el) : null}
                    className={`h-full${isFirstOfLetter ? ' scroll-mt-28' : ''}`}
                  >
                    <EmployeeCard
                      employee={employee}
                      photo={photos[employee.id]}
                      onClick={(emp, rect) => onEmployeeClick?.(emp, rect)}
                      index={index}
                      density={density}
                      isSelected={isSelected(employee.id)}
                      onToggleSelection={onToggleSelection}
                      isFavorite={isFav(employee.id)}
                      onToggleFavorite={onToggleFavorite}
                      hasAnySelected={selectedEmployees.length > 0}
                      onBadgesClick={onBadgesClick}
                      currentUserId={currentUserId}
                    />
                  </div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
      </div>
    );
  }

  // Group by department or office view
  const groupKey = groupByOffice ? 'officeLocation' : 'department';
  const defaultLabel = groupByOffice ? 'No Office' : 'Unassigned';

  const groupedEmployees = employees.reduce((acc, employee) => {
    const group = employee[groupKey] || defaultLabel;
    if (!acc[group]) acc[group] = [];
    acc[group].push(employee);
    return acc;
  }, {});

  const sortedGroups = Object.keys(groupedEmployees).sort();

  return (
    <div className="w-full space-y-10">
      {sortedGroups.map((group) => (
        <motion.section
          key={group}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full space-y-4"
        >
          <div className="flex items-center gap-3">
            {groupByOffice && <Building2 size={16} className="text-ink-3" />}
            <h2 className="text-[15px] font-bold tracking-[-0.01em] text-ink">{group}</h2>
            <span className="font-mono text-[11px] text-ink-4 tabular-nums">
              {groupedEmployees[group].length}
            </span>
            <div className="flex-1 h-px bg-line" />
          </div>

          {displayMode === 'list' ? (
            <div className={`rounded-card overflow-hidden ${LIST_SURFACE}`}>
              {groupedEmployees[group].map((employee, index) => {
                const customProfile = getProfile(employee.id);
                return (
                  <EmployeeListRow
                    key={employee.id}
                    employee={employee}
                    index={index}
                    density={density}
                    photo={customProfile?.photo || employee.openAssetPhoto || photos[employee.id]}
                    isSelected={isSelected(employee.id)}
                    isFavorite={isFav(employee.id)}
                    hasAnySelected={selectedEmployees.length > 0}
                    onBadgesClick={onBadgesClick}
                    currentUserId={currentUserId}
                    onToggleSelection={onToggleSelection}
                    onToggleFavorite={onToggleFavorite}
                    onEmployeeClick={onEmployeeClick}
                  />
                );
              })}
            </div>
          ) : (
            <div
              className="w-full grid gap-3 grid-cols-[repeat(auto-fill,minmax(212px,1fr))]" 
            >
              <AnimatePresence mode="popLayout">
                {groupedEmployees[group].map((employee, index) => (
                  <EmployeeCard
                    key={employee.id}
                    employee={employee}
                    photo={photos[employee.id]}
                    onClick={(emp, rect) => onEmployeeClick?.(emp, rect)}
                    index={index}
                    density={density}
                    isSelected={isSelected(employee.id)}
                    onToggleSelection={onToggleSelection}
                    isFavorite={isFav(employee.id)}
                    onToggleFavorite={onToggleFavorite}
                    hasAnySelected={selectedEmployees.length > 0}
                    onBadgesClick={onBadgesClick}
                    currentUserId={currentUserId}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </motion.section>
      ))}
    </div>
  );
}

export function EmployeeGridSkeleton({ count = 12 }) {
  return (
    <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(212px,1fr))]">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="bg-white dark:bg-zinc-700 rounded-2xl p-6 shadow-md border border-slate-100 dark:border-transparent"
        >
          <div className="flex justify-center mb-4">
            <div className="w-24 h-24 rounded-full skeleton" />
          </div>
          <div className="h-5 skeleton rounded-lg mb-2 mx-4" />
          <div className="h-4 skeleton rounded-lg mb-3 mx-8" />
          <div className="flex justify-center">
            <div className="h-6 w-24 skeleton rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmployeeListSkeleton({ count = 15 }) {
  return (
    <div className="w-full space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-5 py-3 rounded-xl bg-white dark:bg-zinc-800 border border-slate-200 dark:border-transparent shadow-sm"
        >
          <div className="w-10 h-10 rounded-full skeleton flex-shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="h-4 skeleton rounded-md w-40" />
            <div className="h-3 skeleton rounded-md w-28" />
          </div>
          <div className="hidden sm:block h-6 w-24 skeleton rounded-full flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}
